import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";

import { Multi } from "../../navigation/v2/managment/enrollmentRequests/filtrosMatriculas";
import "../../navigation/v2/managment/enrollmentRequests/filtrosMatriculas.css";
import "./buscadorBarra.css";

// Pastilla de filtro de selección múltiple (la misma de Matrículas), para usarla junto al buscador.
export const FiltroMulti = Multi;

// Minúsculas y sin acentos: así se compara lo que escribe la persona contra cualquier texto.
export const normalizar = (texto: string): string =>
	(texto || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();

// Cada palabra debe aparecer en algún lado ("barberia sabado" encuentra "Barbería · Sábados 11:00 am").
export const coincide = (busqueda: string, ...textos: (string | null | undefined)[]): boolean => {
	const palabras = normalizar(busqueda).split(/\s+/).filter(Boolean);
	if (palabras.length < 1) return true;
	const pajar = normalizar(textos.filter(Boolean).join(" "));
	return palabras.every((p) => pajar.includes(p));
};

export interface BuscadorBarraProps {
	valor: string;
	// Se llama al dejar de escribir (350 ms) y al dar Enter; nunca en cada tecla.
	onBuscar: (valor: string) => void;
	placeholder?: string;
	autoFocus?: boolean;
}

// Buscador de los módulos nuevos: un solo borde, halo al enfocar, lupa, × para limpiar. Los filtros van al lado, no dentro.
export const BuscadorBarra: React.FC<BuscadorBarraProps> = ({ valor, onBuscar, placeholder = "Buscar", autoFocus }) => {
	const [texto, setTexto] = useState(valor);
	const ultimo = useRef(valor);
	const reloj = useRef<number | undefined>(undefined);

	useEffect(() => { setTexto(valor); ultimo.current = valor; }, [valor]);
	useEffect(() => () => window.clearTimeout(reloj.current), []);

	const avisar = (v: string) => {
		window.clearTimeout(reloj.current);
		if (v === ultimo.current) return;
		ultimo.current = v;
		onBuscar(v);
	};

	return (
		<label className="waBusca">
			<FontAwesomeIcon icon={solid("magnifying-glass")} className="waBuscaLupa" />
			<input
				type="search"
				value={texto}
				placeholder={placeholder}
				autoFocus={autoFocus}
				maxLength={80}
				onChange={(e) => {
					const v = e.target.value;
					setTexto(v);
					window.clearTimeout(reloj.current);
					reloj.current = window.setTimeout(() => avisar(v), 350);
				}}
				onKeyDown={(e) => { if (e.key === "Enter") avisar(texto); }}
			/>
			{texto !== "" ? (
				<button type="button" className="waBuscaLimpiar" aria-label="Limpiar" onClick={() => { setTexto(""); avisar(""); }}>
					<FontAwesomeIcon icon={solid("xmark")} />
				</button>
			) : ""}
		</label>
	);
};

// Fila de buscador + pastillas de filtro + «Limpiar»; se acomoda en una o varias líneas según el ancho.
export const BarraFiltros: React.FC<{ children: React.ReactNode; hayFiltros?: boolean; onLimpiar?: () => void }> = ({ children, hayFiltros, onLimpiar }) => (
	<div className="waFiltrosBusca">
		{children}
		{hayFiltros && onLimpiar ? <button type="button" className="waBarraLimpiar" onClick={onLimpiar}>Limpiar</button> : ""}
	</div>
);

export default BuscadorBarra;
